import { memo, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { UncontrolledTooltip } from 'reactstrap';
import { UpdateSideNavbarCommunityName } from '../../../stores/layoutStore';
import { IMenuItem, ISectionMenuItem, ISideNavSettings, IsSideNavEmpty } from "../../configuration";
import { ClaimHelper } from "../../utils/helpers";
import { ISideNavStyles } from '../index';
import { RootState } from '../../../stores/store';
import { StyledNavLink, SideNavContainer, SideNavSectionHeader, SideNavTooltipGlobalStyle } from './sideNav.styled';

type SidNavProps = {
    style?: ISideNavStyles;
    sideNavSettings: ISideNavSettings
    userDisplayName?: string;
    isExpanded: boolean;
    hasNewNotification?: boolean;
};

export const SideNav = memo(function SideNav(props: SidNavProps) {
    const dispatch = useDispatch();
    const { communityName, isServiceRequestEnabled } = useSelector((state: RootState) => state.layout);
    const communityId = ClaimHelper.getCommunityId();

    useEffect(() => {
        if (communityId) {
            dispatch(UpdateSideNavbarCommunityName(ClaimHelper.getCommunityName()));
        }
    }, [dispatch, communityId]);


    function GetNavSections(sections: ISectionMenuItem[], additionalclasses?: string | undefined): React.ReactNode | null {
        if (sections?.length <= 0) {
            return null;
        }

        return (
            sections.map((section: ISectionMenuItem, index: number) => (
                GetNavSection(section, index, additionalclasses)
            )))
    }

    function GetNavSection(section: ISectionMenuItem, sectionIndex: number, additionalclasses?: string | undefined): React.ReactNode | null {
        if (section.items?.length <= 0) {
            return null;
        }

        const sectionKey: string = `sideNavItemSection-${sectionIndex}`;
        if (!additionalclasses) {
            additionalclasses = "";
        }

        let className = "pb-3 " + additionalclasses
        if (section.hasTopSeparator) {
            className = className + " border-top";
        }

        if (section.hasBottomSeparator) {
            className = className + " border-bottom";
        }

        return (
            <div key={sectionKey} className={className} style={{ ...section.overrideStyle }}>
                < ul className="nav nav-pills flex-column mb-auto">
                    {GetNavSectionHeader(section, sectionKey)}
                    {section.items.map((item: IMenuItem, itemIndex: number) =>
                        GetNavItem(
                            item.title.toLowerCase() === "service request" && !isServiceRequestEnabled
                                ? { ...item, isDisplayed: false }
                                : item,
                            `${sectionKey}-sideNavItemMain-${itemIndex}`,
                            section.overrideStyle
                        )
                    )}
                </ul>
            </div>
        )
    }

    function GetNavItem(item: IMenuItem, key: string, sectionOverrideStyle?: React.CSSProperties): React.ReactNode | null {
        if (!item.isDisplayed && item.isDisplayed !== undefined) {
            return null;
        }

        let title = item.title;
        let href = item.href ?? "/";

        if (item.title === "Communities" && communityName) {
            title = communityName ?? item.title;
            href = (item.href ?? "") + "/" + communityId;
        }

        return (
            <li key={key} className="nav-item py-0 my-0 mx-2" id={key}>
                <StyledNavLink to={href} className="d-flex align-items-center w-100 ps-4 pe-4 py-2" style={({ isActive }) => ({
                    ...(props.style?.sideNavItem ?? {}),
                    ...(sectionOverrideStyle ?? {}),
                    ...(isActive ? props.style?.sideNavItemActive : {}),
                    ...(isActive ? item.overrideActiveStyle : item.overrideStyle),
                })} >
                    <span className={item.icon + " me-3 position-relative"}>
                        {item.hasAlert && (
                            <span className="position-absolute top-0 start-100 translate-middle p-2 bg-danger border border-light rounded-circle">
                                <span className="visually-hidden">{item.alertTitle}</span>
                            </span>
                        )}
                    </span>

                    <span style={props.isExpanded ? props.style?.sideNavItemTextExpanded : props.style?.sideNavItemTextCollapsed} >
                        {title}
                    </span>
                </StyledNavLink>

                {!props.isExpanded && (
                    <UncontrolledTooltip
                        className="sideNav-tooltip"
                        placement="right"
                        autohide={false}
                        target={key}
                        style={props.style?.sideNavItemTextTooltip}
                    >
                        {item.title}
                    </UncontrolledTooltip>
                )}
            </li>
        );
    }

    function GetNavSectionHeader(section: ISectionMenuItem, key: string): React.ReactNode {
        const hasIcon = section.icon !== null && section.icon !== undefined;

        return (
            <li key={key} className="nav-item mx-0 my-0">
                {(props.isExpanded && <SideNavSectionHeader className="ms-1 ps-2 pt-2" style={section.overrideStyle}>{(hasIcon && <span className={section.icon + " me-2 position-relative"} />)}<span>{section.title}</span></SideNavSectionHeader>)}
            </li>
        )
    }

    if (IsSideNavEmpty(props.sideNavSettings)) {
        return null;
    }

    return (
        <>
            <SideNavTooltipGlobalStyle />
            <SideNavContainer
                $isExpanded={props.isExpanded}
                className="d-flex flex-column flex-shrink-0 pt-1 overflow-y-auto"
                style={{ ...props.style?.sideNavContainer, }}
            >
                <div>
                    {GetNavSections(props.sideNavSettings.mainSections)}
                </div>
                <div className="mt-auto">
                    {GetNavSections(props.sideNavSettings.footerSections)}
                    <div className="d-block d-md-none">
                        {GetNavSections(props.sideNavSettings.mobileSections)}
                    </div>
                </div>
            </SideNavContainer>
        </>
    )
});